import { QueryResult, useMutation } from '@apollo/client';
import {
  ArrayHelpers,
  Field,
  FieldArray,
  Formik,
  FormikValues
} from 'formik';
import React, { Fragment, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import AsyncCreatableSelect from 'react-select/async-creatable';
import Select from 'react-select';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import mascotHappy from '../../../assets/svg/onboarding/mascot-happy.svg';
import trashDangerIcon from '../../../assets/svg/trash-danger.svg';
import globeGrey from '../../../assets/images/project/globe-grey.svg';
import globeRed from '../../../assets/images/project/globe-red.svg';
import usersGrey from '../../../assets/images/project/users-grey.svg';
import usersRed from '../../../assets/images/project/users-red.svg';
import coinGrey from '../../../assets/svg/coin-grey.svg';
import coinWhite from '../../../assets/svg/coin-white.svg';
import heartGrey from '../../../assets/svg/heart-grey.svg';
import heartRed from '../../../assets/svg/heart-red.svg';
import searchIcon from '../../../assets/svg/search.svg';
import { DELETE_PROJECT } from '../../../graphql/DELETE_PROJECT';
import { GET_PROFESSIONS_BY_FILTER_QUERY } from '../../../graphql/GET_PROFESSIONS_BY_FILTER_QUERY';
import { UPDATE_PROJECT } from '../../../graphql/UPDATE_PROJECT';
import {
  useAutocomplete
} from '../../../hooks/useAutocomplete';
import { IClientUser } from '../../../interfaces/user';
import { validVideoUrlRegex } from '../../../services/videoUrlParser';
import {
  saveAIPoolToSession,
  loadAIPoolFromSession,
  GeneratedImagePool
} from '../../../utils/generatedImageCache';
import * as projectConstants from './constants';
import GenerateDescriptionForm from './GenerateDescriptionForm';
import ImageUploadModal from './Steps/ImageUploadModal';
import FormField from '../../FormField';
import { GET_PROJECT_getProject } from '../../../graphql/types/GET_PROJECT';

export interface IUpdateProjectProps extends IClientUser {
  project: GET_PROJECT_getProject;
}

const projectValidationSchema = Yup.object().shape({
  description: Yup.string().required('This field is required'),
  title: Yup.string().required('This field is required'),
  type: Yup.string().required('This field is required'),
  isPublic: Yup.string().required('This field is required'),
  videoUrl: Yup.string()
    .test(
      'is-url-valid',
      'Input must be a valid Youtube or Vimeo video URL.',
      (value) => {
        if (!value) return true;
        return validVideoUrlRegex.test(value);
      }
    )
    .nullable(),
  country: Yup.string()
});

const UpdateProject: React.FC<IUpdateProjectProps> = (props) => {
  const history = useHistory();
  const [deleteProjectModalIsActive, setDeleteProjectModalIsActive] =
    useState<boolean>(false);
  const [savedMedia, setSavedMedia] = useState<any[]>(
    props.project.medias || []
  );
  const [savedGenres, setSavedGenres] = useState<string[]>(
    props.project.genres && props.project.genres.length > 0
      ? props.project.genres.map((genre: any) => genre.name)
      : []
  );
  const [genreSearchInput, setGenreSearchInput] = useState('');
  const [showGenreDropdown, setShowGenreDropdown] = useState(false);
  const [showFundingDropdown, setShowFundingDropdown] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [imageUploadMode, setImageUploadMode] = useState<
    'ai' | 'manual' | null
  >(props.project.medias && props.project.medias.length > 0 ? 'manual' : null);

  const [aiGeneratedPool, setAiGeneratedPool] = useState<
    GeneratedImagePool | undefined
  >(() => loadAIPoolFromSession(props.project.id));
  const genreInputRef = React.useRef<HTMLInputElement>(null);
  const genreDropdownRef = React.useRef<HTMLDivElement>(null);
  const fundingDropdownRef = React.useRef<HTMLDivElement>(null);
  const fundingButtonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        genreDropdownRef.current &&
        !genreDropdownRef.current.contains(event.target as Node) &&
        genreInputRef.current &&
        !genreInputRef.current.contains(event.target as Node)
      ) {
        setShowGenreDropdown(false);
      }

      if (
        fundingDropdownRef.current &&
        !fundingDropdownRef.current.contains(event.target as Node) &&
        fundingButtonRef.current &&
        !fundingButtonRef.current.contains(event.target as Node)
      ) {
        setShowFundingDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const deleteProjectCompleted = (): void => {
    void toast.success('Project successfully deleted.');
    setTimeout(() => {
      history.replace('/');
    }, 100);
  };

  const updateProjectCompleted = (data: any): void => {
    void toast.success('Project successfully updated!');
    setTimeout(() => {
      history.push(`/project/${data.editProject.id}`);
    }, 100);
  };

  const [deleteProject] = useMutation<QueryResult>(DELETE_PROJECT, {
    onCompleted: deleteProjectCompleted,
    refetchQueries: ['GET_PROJECT']
  });

  const cancelProjectUpdate = (): void => {
    if (props.project && props.project.id) {
      history.push(`/project/${props.project.id}`);
      return;
    }
    history.push('/');
  };

  const [updateProject] = useMutation(UPDATE_PROJECT, {
    onCompleted: updateProjectCompleted,
    refetchQueries: ['GET_PROJECT']
  });

  const handleDeleteProject = async () => {
    await deleteProject({
      variables: {
        projectId: parseInt(props.project.id, 10)
      }
    });
  };

  const deleteProjectModal = () => {
    return (
      <div className={`modal ${deleteProjectModalIsActive && 'is-active'}`}>
        <div
          className="modal-background"
          onClick={() => setDeleteProjectModalIsActive(false)}
        />
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Delete this project?</p>
          </header>
          <section className="modal-card-body">
            <p>
              This action cannot be undone. All project data will be permanently
              deleted.
            </p>
          </section>
          <footer className="modal-card-foot selection-modal-buttons">
            <button
              type="button"
              className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-outlined"
              onClick={() => setDeleteProjectModalIsActive(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-danger"
              onClick={handleDeleteProject}
            >
              Delete Project
            </button>
          </footer>
        </div>
      </div>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderCollaborationForm = (
    arrayHelpers: ArrayHelpers,
    collabIdx: number,
    values: FormikValues,
    handleBlur: (e: any) => void,
    handleChange: (eventOrTextValue: React.ChangeEvent<any>) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => any
  ) => {
    const collab = values.collaborations[collabIdx];
    return (
        <div key={collabIdx} className="box mb-4 is-shadowless" style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', backgroundColor: '#F9FAFB' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span style={{ color: '#374151' }}>Collaborator {collabIdx + 1}</span>
                <span
                    className="is-clickable"
                    style={{ color: '#EF4444', cursor: 'pointer' }}
                    onClick={() => arrayHelpers.remove(collabIdx)}
                >
                    Remove
                </span>
            </div>
            <style>{`
                @media (max-width: 768px) {
                    .custom-mobile-col-top { margin-bottom: 0.75rem !important; }
                    .custom-mobile-col-bottom { padding-top: 0 !important; }
                }
            `}</style>
            <div className="columns" style={{ marginBottom: 0 }}>
                <div className="column is-half custom-mobile-col-top" style={{ paddingBottom: 0 }}>
                    <div className="field" style={{ marginBottom: 0 }}>
                        <label className="label">Profession</label>
                        <div className="control">
                            <AsyncCreatableSelect
                                cacheOptions={true}
                                isClearable={true}
                                loadOptions={loadOptions}
                                placeholder="Designer"
                                onChange={(newValue: any) => {
                                    setFieldValue(`collaborations.${collabIdx}.professions[0]`, newValue ? newValue.label : '');
                                }}
                                value={
                                    collab.professions && collab.professions[0]
                                        ? { label: collab.professions[0], value: collab.professions[0] }
                                        : null
                                }
                                styles={{
                                    control: (base: any) => ({
                                        ...base,
                                        borderColor: '#E5E7EB',
                                        boxShadow: 'none',
                                        fontSize: '14px'
                                    })
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="column is-half custom-mobile-col-bottom" style={{ paddingBottom: 0 }}>
                    <div className="field" style={{ marginBottom: 0 }}>
                        <label className="label">Compensation</label>
                        <div className="control">
                            <Select
                                options={projectConstants.collaboratorCompensationTypeOptions}
                                value={projectConstants.collaboratorCompensationTypeOptions.find(opt => opt.value === values.collaborations[collabIdx].compensation)}
                                onChange={(option: any) => { setFieldValue(`collaborations.${collabIdx}.compensation`, option.value); }}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: '#E5E7EB',
                                        boxShadow: 'none',
                                        fontSize: '14px'
                                    })
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="field" style={{ marginTop: '0.75rem' }}>
                <label className="label">Profession You Are Looking For</label>
                <div className="control">
                    <Field
                        name={`collaborations.${collabIdx}.additionalRequirements`}
                        className="input has-background-white"
                        placeholder="Designer"
                        style={{ borderColor: '#E5E7EB', boxShadow: 'none' }}
                    />
                </div>
            </div>
        </div>
    );
  };

  const { loadOptions } = useAutocomplete({
    query: GET_PROFESSIONS_BY_FILTER_QUERY
  });

  const getInitialValues = () => {
    return {
      collaborations: (props.project.collaborations ?? []).map((collab) => ({
        additionalRequirements: collab.additionalRequirements,
        compensation: collab.compensation,
        id: collab.id ? collab.id : undefined,
        professions: ((collab.requiredProfessions && collab.requiredProfessions.length > 0)
          ? collab.requiredProfessions
          : collab.requiredSkills ?? []).map((skill) => skill.name)
      })),
      description: props.project.description,
      fundingStatus: props.project.fundingStatus,
      genres: savedGenres,
      country: props.project.locations && props.project.locations.length > 0
        ? props.project.locations[0].name
        : '',
      onlyCreatorsBasedInLocation: false,
      newLocation: '',
      medias: savedMedia,
      progress: props.project.progress,
      requiredNewSkill: '',
      title: props.project.title,
      type: props.project.type,
      videoUrl: props.project.videoUrl,
      isPublic:
        props.project.isPublic
          ? projectConstants.projectShareableStatus.PUBLIC
          : projectConstants.projectShareableStatus.PRIVATE
    };
  };

  const initialValues = useMemo(() => getInitialValues(), [props.project.id]);

  return (
    <Formik
      validationSchema={projectValidationSchema}
      enableReinitialize={false}
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting }) => {
        const valuesToSubmit = {
          title: values.title,
          description: values.description,
          type: values.type,
          progress: values.progress,
          fundingStatus: values.fundingStatus,
          videoUrl: values.videoUrl === '' ? null : values.videoUrl,
          isPublic: values.isPublic === projectConstants.projectShareableStatus.PUBLIC,
          genres: values.genres,
          locations: values.country ? [values.country] : [],
          medias: values.medias.map((media) => parseInt(media.id, 10)),
          collaborations: values.collaborations.map((collab: any) => {
            const sanitizeObj = {
              additionalRequirements: collab.additionalRequirements,
              compensation: collab.compensation,
              currency: 'JPY',
              professions: [],
              skills: collab.professions
            };
            if (collab.id) {
              return {
                ...sanitizeObj,
                id: collab.id
              };
            }
            return sanitizeObj;
          })
        };

        try {
          await updateProject({
            variables: {
              id: parseInt(props.project.id, 10),
              ...valuesToSubmit
            }
          });
        } catch (e: any) {
          console.error(e);
          toast.error('Failed to update project: ' + (e.message || 'Please try again.'));
          setSubmitting(false);
          return;
        }

        setSubmitting(false);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        errors,
        touched
      }) => {
        const filteredGenreOptions = genreSearchInput
          ? projectConstants.projectGenreOptions.filter((genre) =>
              genre.label.toLowerCase().includes(genreSearchInput.toLowerCase())
            )
          : projectConstants.projectGenreOptions;

        const availableGenreOptions = filteredGenreOptions.filter(
          (genre) => !values.genres.includes(genre.value)
        );

        const exactMatch = projectConstants.projectGenreOptions.some(
          (genre) => genre.label.toLowerCase() === genreSearchInput.trim().toLowerCase()
        );
        const showCreateOption = genreSearchInput.trim() !== '' && !exactMatch && !values.genres.includes(genreSearchInput.trim());

        return (
          <div className="update-project-page new-project-wizard">
            <form onSubmit={handleSubmit}>
              <div className="columns section page-add-project-container no-bottom-margin">
                <div className="column">
                  <div className="project-review-header is-rounded is-dropshadowed">
                    <div className="project-review-header-content">
                      <div className="project-review-header-left">
                        <h1>Update Your Project Details</h1>
                        <p>Review and update your project information</p>
                      </div>
                      <div className="project-review-header-right">
                        <img src={mascotHappy} alt="Mascot" />
                      </div>
                    </div>
                  </div>

                  <div className="card page-add-project-card is-rounded is-dropshadowed">
                    <div className="card-content page-add-project-card-content">
                      <div className="project-title-section">
                        <h2 className="project-dynamic-title">
                          {values.title || 'Your Project'}
                        </h2>
                      </div>

                      <div className="field mb-5">
                        <label className="label">
                          Project Title{' '}
                          <span className="has-text-danger">*</span>
                        </label>
                        <div className="control">
                          <FormField
                            name="title"
                            type="text"
                            placeholder="Title"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.title}
                            additionalInputClasses="has-background-white"
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div className="field mb-5">
                        <label className="label">
                          Description <span className="has-text-danger">*</span>
                        </label>
                        <div className="description-header-row">
                          <p className="description-helper-text">
                            Feel free to edit what's generated with Matchhat
                          </p>
                          <GenerateDescriptionForm
                            onDescriptionGenerated={(generatedDescription) => {
                              setFieldValue(
                                'description',
                                generatedDescription
                              );
                            }}
                            formValues={values}
                          />
                        </div>
                        <div className="control">
                          <FormField
                            multiline={true}
                            autoResize={true}
                            name="description"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Describe your project, what you want to accomplish, and why people should join your project."
                            value={values.description}
                            additionalInputClasses="has-background-white"
                          />
                        </div>
                      </div>

                      {/* Type */}
                      <div className="field mb-5">
                        <label className="label">
                          Type <span className="has-text-danger">*</span>
                        </label>
                        <div className="control switchradio is-flex-desktop is-block-mobile">
                          {projectConstants.projectTypeOptions.map(
                            (projectType) => (
                              <Fragment key={projectType.value}>
                                <input
                                  type="radio"
                                  value={projectType.value}
                                  id={projectType.value}
                                  name="type"
                                  className="switchradio-input is-absolute"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  checked={values.type === projectType.value}
                                  required={true}
                                />
                                <label
                                  htmlFor={projectType.value}
                                  className="button has-text-weight-bold switchradio-label p-t-1 p-b-1"
                                >
                                  <img
                                    src={
                                      projectType.value === 'PASSION'
                                        ? values.type === projectType.value
                                          ? heartRed
                                          : heartGrey
                                        : values.type === projectType.value
                                        ? coinWhite
                                        : coinGrey
                                    }
                                    alt=""
                                    className="is-inline image"
                                  />
                                  <span className="is-small">
                                    {projectType.label}
                                  </span>
                                </label>
                              </Fragment>
                            )
                          )}
                        </div>
                      </div>

                      {/* Share With */}
                      <div className="field mb-5">
                        <label className="label">Share With  <span className="has-text-danger">*</span></label>
                        <div className="control switchradio is-flex-desktop is-block-mobile">
                          {projectConstants.projectShareableOptions.map(
                            (shareableStatus) => (
                              <Fragment key={shareableStatus.value}>
                                <Field
                                  type="radio"
                                  value={shareableStatus.value}
                                  id={shareableStatus.value}
                                  name="isPublic"
                                  className="switchradio-input is-absolute"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  checked={
                                    values.isPublic === shareableStatus.value
                                  }
                                  required={true}
                                />
                                <label
                                  htmlFor={shareableStatus.value}
                                  className="button has-text-weight-bold switchradio-label p-t-1 p-b-1"
                                >
                                  <img
                                    src={
                                      shareableStatus.value ===
                                      projectConstants.projectShareableStatus
                                        .PUBLIC
                                        ? values.isPublic ===
                                          shareableStatus.value
                                          ? globeRed
                                          : globeGrey
                                        : values.isPublic ===
                                          shareableStatus.value
                                        ? usersRed
                                        : usersGrey
                                    }
                                    alt=""
                                    className="is-inline image"
                                  />
                                  <span className="is-small">
                                    {shareableStatus.label}
                                  </span>
                                </label>
                              </Fragment>
                            )
                          )}
                        </div>
                      </div>

                      {/* I'm looking for */}
                      <div className="field mb-5">
                        <label className="label">I'm Looking For</label>
                        <FieldArray name="collaborations">
                          {(arrayHelpers) => (
                            <div className="collaborations-section mt-3">
                              {values.collaborations &&
                                values.collaborations.length > 0 &&
                                values.collaborations.map(
                                  (collab, collabIdx: number) => {
                                    return renderCollaborationForm(
                                      arrayHelpers,
                                      collabIdx,
                                      values,
                                      handleBlur,
                                      handleChange,
                                      setFieldValue
                                    );
                                  }
                                )}
                              <div className="control control-collaboration">
                                <div
                                  className="is-clickable has-text-weight-semibold mb-2 mt-1"
                                  style={{ color: '#5775FF', cursor: 'pointer' }}
                                  onClick={() => {
                                    return arrayHelpers.push({
                                      additionalRequirements: '',
                                      compensation:
                                        projectConstants
                                          .collaboratorCompensationTypeOptions[0]
                                          .value,
                                      professions: []
                                    });
                                  }}
                                >
                                  + Add collaborator
                                </div>
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </div>

                      {/* Country */}
                      <div className="field mb-5">
                        <label className="label">Country</label>
                        <div className="control">
                          <input
                            type="text"
                            className="input has-background-white"
                            name="country"
                            placeholder="Enter your country"
                            value={values.country}
                            onChange={(e) => {
                              handleChange(e);
                              if (!e.target.value) {
                                setFieldValue('onlyCreatorsBasedInLocation', false);
                              }
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                        <div className="field mt-4">
                          <label className={`checkbox${!values.country ? ' is-disabled' : ''}`}>
                            <Field
                              type="checkbox"
                              name="onlyCreatorsBasedInLocation"
                              checked={values.onlyCreatorsBasedInLocation}
                              onChange={handleChange}
                              disabled={!values.country}
                            />
                            <span className="ml-2">
                              Only creators based in {values.country || 'this country'}
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Genre */}
                      <div className="field mb-5">
                        <label className="label">Genre</label>
                        <div className="project-tags-input-container">
                          <div className="selected-tags-wrapper">
                            {values.genres &&
                              values.genres.length > 0 &&
                              values.genres.map((genreValue) => {
                                const genreOption =
                                  projectConstants.projectGenreOptions.find(
                                    (g) => g.value === genreValue
                                  );
                                return (
                                  <div
                                    key={genreValue}
                                    className="selected-tag-chip"
                                  >
                                    <span className="tag-text">
                                      {genreOption
                                        ? genreOption.label
                                        : genreValue}
                                    </span>
                                    <button
                                      type="button"
                                      className="tag-remove-btn"
                                      onClick={() => {
                                        const updatedGenres =
                                          values.genres.filter(
                                            (g) => g !== genreValue
                                          );
                                        setFieldValue('genres', updatedGenres);
                                        setSavedGenres(updatedGenres);
                                      }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}

                            <div className="tag-search-input-wrapper">
                              <img
                                src={searchIcon}
                                alt="Search"
                                className="tag-search-icon"
                              />
                              <input
                                ref={genreInputRef}
                                type="text"
                                className="tag-search-input"
                                placeholder="Search genres"
                                value={genreSearchInput}
                                onChange={(e) => {
                                  setGenreSearchInput(e.target.value);
                                  setShowGenreDropdown(true);
                                }}
                                onFocus={() => setShowGenreDropdown(true)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    let newGenre = '';
                                    if (availableGenreOptions.length > 0) {
                                      newGenre = availableGenreOptions[0].value;
                                    } else if (genreSearchInput.trim() !== '') {
                                      newGenre = genreSearchInput.trim();
                                    }

                                    if (newGenre && !values.genres.includes(newGenre)) {
                                      const updatedGenres = [
                                        ...values.genres,
                                        newGenre
                                      ];
                                      setFieldValue('genres', updatedGenres);
                                      setSavedGenres(updatedGenres);
                                      setGenreSearchInput('');
                                      setShowGenreDropdown(false);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setShowGenreDropdown(false);
                                  }
                                }}
                              />
                            </div>

                            {showGenreDropdown &&
                              genreSearchInput &&
                              (availableGenreOptions.length > 0 || showCreateOption) && (
                                <div
                                  ref={genreDropdownRef}
                                  className="genre-dropdown"
                                >
                                  {availableGenreOptions.map((genre) => (
                                    <div
                                      key={genre.value}
                                      className="genre-dropdown-item"
                                      onClick={() => {
                                        const updatedGenres = [
                                          ...values.genres,
                                          genre.value
                                        ];
                                        setFieldValue('genres', updatedGenres);
                                        setSavedGenres(updatedGenres);
                                        setGenreSearchInput('');
                                        setShowGenreDropdown(false);
                                        genreInputRef.current?.focus();
                                      }}
                                    >
                                      {genre.label}
                                    </div>
                                  ))}
                                  {showCreateOption && (
                                    <div
                                      className="genre-dropdown-item"
                                      onClick={() => {
                                        const newGenre = genreSearchInput.trim();
                                        const updatedGenres = [
                                          ...values.genres,
                                          newGenre
                                        ];
                                        setFieldValue('genres', updatedGenres);
                                        setSavedGenres(updatedGenres);
                                        setGenreSearchInput('');
                                        setShowGenreDropdown(false);
                                        genreInputRef.current?.focus();
                                      }}
                                    >
                                      Create "{genreSearchInput.trim()}"
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      {/* Video URL */}
                      <div className="field mb-5">
                        <label className="label">Video URL</label>
                        <div className="control">
                          <FormField
                            name="videoUrl"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter URL"
                            value={values.videoUrl}
                            additionalInputClasses="has-background-white"
                          />
                        </div>
                      </div>

                      {/* Storyboard Image */}
                      <div className="field mb-5">
                        <label className="label">Storyboard Image</label>
                        <div className="level">
                          <div className="level-left"></div>
                          <div className="level-right">
                            <button
                              type="button"
                              className="button generate-description-button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary"
                              onClick={() => {
                                setShowImageUploadModal(true);
                                setTimeout(() => {
                                  if (window.innerWidth <= 768) {
                                    window.scrollTo({
                                      top: 0,
                                      behavior: 'smooth'
                                    });
                                  }
                                }, 100);
                              }}
                            >
                              Upload or Generate Images
                            </button>
                          </div>
                        </div>

                        {/* Image Preview Grid */}
                        {values.medias && values.medias.length > 0 && (
                          <div className="project-preview">
                            <div className="preview-content-card">
                              <div className="preview-images-grid mb-4">
                                {values.medias.map((media, idx) => (
                                  <div
                                    key={media.id}
                                    className="preview-image-item"
                                    onMouseEnter={(e) => {
                                      const deleteBtn =
                                        e.currentTarget.querySelector(
                                          '.delete-overlay'
                                        ) as HTMLElement;
                                      if (deleteBtn) {
                                        deleteBtn.style.opacity = '1';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const deleteBtn =
                                        e.currentTarget.querySelector(
                                          '.delete-overlay'
                                        ) as HTMLElement;
                                      if (deleteBtn) {
                                        deleteBtn.style.opacity = '0';
                                      }
                                    }}
                                  >
                                    <img
                                      src={media.fileUrl}
                                      alt={`Project image ${idx + 1}`}
                                    />
                                    <div
                                      className="delete-overlay"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const updatedMedias =
                                          values.medias.filter(
                                            (m) => m.id !== media.id
                                          );
                                        setFieldValue('medias', updatedMedias);
                                        setSavedMedia(updatedMedias);
                                        setAiGeneratedPool((prevPool) => {
                                          if (!prevPool?.selectedIds) {
                                            return prevPool;
                                          }

                                          const updatedPool = {
                                            ...prevPool,
                                            selectedIds:
                                              prevPool.selectedIds.filter(
                                                (id) => id !== media.id
                                              )
                                          };

                                          saveAIPoolToSession(props.project.id, updatedPool);

                                          return updatedPool;
                                        });
                                      }}
                                    >
                                      <button type="button">Delete</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Funding */}
                      <div className="field mb-5 funding-field-spacing">
                        <label className="label">
                          Funding
                        </label>
                        <div className="control funding-control">
                          <button
                            ref={fundingButtonRef}
                            type="button"
                            className="funding-button"
                            onClick={() =>
                              setShowFundingDropdown(!showFundingDropdown)
                            }
                          >
                            <span>
                              {projectConstants.projectFundingStatusOptions.find(
                                (f) => f.value === values.fundingStatus
                              )?.label ?? 'Select funding status'}
                            </span>
                            <span
                              className={`funding-button-arrow ${
                                showFundingDropdown ? 'is-open' : ''
                              }`}
                            >
                              ▼
                            </span>
                          </button>

                          {showFundingDropdown && (
                            <div
                              ref={fundingDropdownRef}
                              className="funding-dropdown"
                              style={{
                                top: fundingButtonRef.current
                                  ? `${
                                      fundingButtonRef.current.getBoundingClientRect()
                                        .bottom + 8
                                    }px`
                                  : 'auto',
                                left: fundingButtonRef.current
                                  ? `${
                                      fundingButtonRef.current.getBoundingClientRect()
                                        .left
                                    }px`
                                  : 'auto',
                                width: fundingButtonRef.current
                                  ? `${
                                      fundingButtonRef.current.getBoundingClientRect()
                                        .width
                                    }px`
                                  : 'auto'
                              }}
                            >
                              {projectConstants.projectFundingStatusOptions.map(
                                (funding) => {
                                  const isSelected =
                                    values.fundingStatus === funding.value;
                                  return (
                                    <div
                                      key={funding.value}
                                      className={`funding-dropdown-item ${
                                        isSelected ? 'is-selected' : ''
                                      }`}
                                      onClick={() => {
                                        setFieldValue(
                                          'fundingStatus',
                                          funding.value
                                        );
                                        setShowFundingDropdown(false);
                                      }}
                                    >
                                      {isSelected && (
                                        <span className="dropdown-checkmark">
                                          ✓
                                        </span>
                                      )}
                                      <span>{funding.label}</span>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer with buttons */}
                    <div className="page-add-project-controls">
                      <div className="container page-add-project-controls-container update-project-footer-buttons">
                        <button
                          type="button"
                          className="button is-fullwidth-mobile is-borderless page-add-project-controls-button has-background-white-ter has-text-weight-semibold has-text-danger"
                          onClick={() => setDeleteProjectModalIsActive(true)}
                        >
                          <img
                            src={trashDangerIcon}
                            alt="trash danger icon"
                            className="img icon"
                          />
                          <span className="is-small">Delete Project</span>
                        </button>
                        {deleteProjectModal()}

                        <button
                          type="button"
                          className="button is-fullwidth-mobile is-borderless page-add-project-controls-button has-background-white-ter has-text-weight-semibold has-text-primary"
                          onClick={() => {
                            cancelProjectUpdate();
                          }}
                        >
                          <span className="is-small">Cancel</span>
                        </button>

                        <button
                          type="button"
                          className="button is-fullwidth-mobile is-borderless page-add-project-controls-button has-background-white-ter has-text-weight-semibold has-text-primary"
                        >
                          <span className="is-small">Save Draft</span>
                        </button>

                        <button
                          type="submit"
                          className={`button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary ${
                            isSubmitting && 'is-loading'
                          }`}
                          disabled={isSubmitting}
                        >
                          <span className="is-small">Update</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            {/* Image Upload Modal */}
            <ImageUploadModal
              isOpen={showImageUploadModal}
              onClose={() => setShowImageUploadModal(false)}
              onComplete={(medias, mode, aiPool) => {
                setFieldValue('medias', medias);
                setImageUploadMode(mode);
                setAiGeneratedPool(aiPool);

                saveAIPoolToSession(props.project.id, aiPool);

                setShowImageUploadModal(false);
              }}
              projectIdea=""
              existingMode={imageUploadMode}
              existingMedias={values.medias}
              existingAIPool={aiGeneratedPool}
            />
          </div>
        );
      }}
    </Formik>
  );
};

export default UpdateProject;
